import { useEffect } from "react";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { PLATFORM_MODULES } from "@miplatform/constants";
import { MiTHEME } from "@miplatform/theme";
import { MiConfigHeaderLogo } from "./components/MiConfigHeaderLogo";
import { configNavItems } from "./routes/config";
import { configRoutes } from "./routes/config/configRoutes";
import { legalRoutes } from "./routes/legal";
import {
  generateNavItemsByPermissions,
  generateRoutesByPermissions,
} from "./utils/filterRoutes/filterRoutes";
import { NhcBoard } from "./views/NewHomeConstruction/NhcBoard";
import { Schedule } from "./views/Schedule/Schedule";
import { useMyCalendarSets, usePermissions } from "./hooks";
import { MainLayout } from "./layouts";
import {
  accountingRoutes,
  adminNavItems,
  adminSettingsRoutes,
  dispatchRoutes,
  estimatingRoutes,
  inspectionsRoutes,
  navItems,
  routes,
  schedulingRoutes,
} from "./routes";
import { Inspections } from "./views";

const CALENDAR_SETS = "Calendar Sets";

export const AuthorizedRoutes = () => {
  const { myCalendarSets, getMyCalendarSets } = useMyCalendarSets();
  const { permissions } = usePermissions();

  useEffect(() => {
    getMyCalendarSets();
  }, []);

  const dispatchCalendarNavItems = myCalendarSets
    .filter(
      (calendarSet) => calendarSet?.module === PLATFORM_MODULES.MI_DISPATCH
    )
    .map((calendarSet, index) => {
      return {
        id: calendarSet.id,
        name: calendarSet.name,
        path: `/dispatch/${calendarSet.id}`,
        testId: `dispatch-nav-item-calendar-option-${index}`,
        element: <Schedule />,
        group: CALENDAR_SETS,
      };
    });

  const schedulingCalendarNavItems = myCalendarSets
    .filter(
      (calendarSet) => calendarSet.module === PLATFORM_MODULES.MI_SCHEDULING
    )
    .map((calendarSet, index) => {
      return {
        id: calendarSet.id,
        name: calendarSet.name,
        path: `/scheduling/${calendarSet.id}`,
        testId: `scheduling-nav-item-calendar-option-${index}`,
        element: <NhcBoard />,
        group: CALENDAR_SETS,
      };
    });

  const inspectionCalendarNavItems = myCalendarSets
    .filter(
      (calendarSet) => calendarSet.module === PLATFORM_MODULES.MI_INSPECTIONS
    )
    .map((calendarSet, index) => {
      return {
        id: calendarSet.id,
        name: calendarSet.name,
        path: `/inspections/${calendarSet.id}`,
        testId: `inspections-nav-item-calendar-option-${index}`,
        element: <Inspections />,
        group: "Inspections",
      };
    });

  const mainView = (
    <MainLayout
      sideNavItems={generateNavItemsByPermissions(
        navItems(
          dispatchCalendarNavItems,
          schedulingCalendarNavItems,
          inspectionCalendarNavItems
        ),
        permissions
      )}
      refreshOnNavigate={getMyCalendarSets}
    />
  );

  const settingsView = (
    <MainLayout
      sideNavItems={generateNavItemsByPermissions(adminNavItems, permissions)}
    />
  );
  const configView = (
    <MainLayout
      sideNavItems={generateNavItemsByPermissions(configNavItems, permissions)}
      appBarColor={MiTHEME.palette.grey.main}
      appLogo={<MiConfigHeaderLogo />}
      hideSearchBar
    />
  );

  const router = createBrowserRouter([
    {
      path: "/",
      element: mainView,
      children: routes,
    },
    // MiDispatch & Calendar Sets
    {
      path: "",
      element: mainView,
      children: generateRoutesByPermissions(dispatchRoutes, permissions),
    },
    {
      path: "dispatch",
      element: mainView,
      children: generateRoutesByPermissions(
        [
          {
            path: "/dispatch/:id",
            element: <Schedule />,
          },
        ],
        permissions
      ),
    },
    // MiScheduling
    {
      path: "",
      element: mainView,
      children: generateRoutesByPermissions(schedulingRoutes, permissions),
    },
    {
      path: "scheduling",
      element: mainView,
      children: generateRoutesByPermissions(
        [
          {
            path: "/scheduling/:calendarSetId/:calendarId",
            element: <NhcBoard />,
          },
          {
            path: "/scheduling/:calendarSetId",
            element: <NhcBoard />,
          },
        ],
        permissions
      ),
    },
    // MiInspections
    {
      path: "inspections",
      element: mainView,
      children: generateRoutesByPermissions(inspectionsRoutes, permissions),
    },
    // MiAccounting
    {
      path: "accounting",
      element: mainView,
      children: generateRoutesByPermissions(accountingRoutes, permissions),
    },
    // MiEstimating
    {
      path: "estimating",
      element: mainView,
      children: generateRoutesByPermissions(estimatingRoutes, permissions),
    },
    // Settings - Admin
    {
      path: "settings",
      element: settingsView,
      children: generateRoutesByPermissions(adminSettingsRoutes, permissions),
    },
    // Legal
    {
      path: "legal",
      element: mainView,
      children: generateRoutesByPermissions(legalRoutes, permissions),
    },
    // Config
    {
      path: "config",
      element: configView,
      children: generateRoutesByPermissions(configRoutes, permissions),
    },
    // Error handling
    {
      path: "*",
      element: (
        <MainLayout
          isError
          sideNavItems={navItems(
            dispatchCalendarNavItems,
            schedulingCalendarNavItems
          )}
        />
      ),
    },
    {
      path: "settings/*",
      element: <MainLayout isError sideNavItems={adminNavItems} />,
    },
    {
      path: "config/*",
      element: (
        <MainLayout
          isError
          sideNavItems={configNavItems}
          appBarColor={MiTHEME.palette.grey.main}
          appLogo={<MiConfigHeaderLogo />}
          hideSearchBar
        />
      ),
    },
  ]);
  return <RouterProvider router={router} />;
};
